import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import React, { useEffect, useMemo, useState } from 'react'
import {
  PILLAR_NS_MAP,
  PILLAR_PAGE_MAP,
  PillarPage,
  SearchPageCity
} from 'src/constants/searchPages'
import useEffectiveCity from 'src/hooks/useEffectiveCity'
import { IPost } from 'src/types/hygraph'
import Hero from './Hero'

// Always-rendered sections — direct imports so SSR and CSR emit the same
// tree at hydration time (React 19 streaming Suspense can otherwise
// tree-shift a `dynamic()` placeholder and trigger #418).
import CrossLinkLogistics from 'src/components/PersonalizedLanding/CrossLinkLogistics'
import CrossLinkOnDemand from 'src/components/PersonalizedLanding/CrossLinkOnDemand'
import PillarDisambiguation from 'src/components/PillarDisambiguation'
import { bandForPillar, captionForPillar } from 'src/constants/logoBands'
import Logos from '../Landing/Logos'
import RecommendedSection from '../Landing/RecommendedSection'
import Comparison from './Comparison'
import FaqsSection from './FaqsSection'
import Features from './Features'

// Conditionally-rendered sections stay dynamic — gated by `isPaidWithCity`
// / `isB2B` / `showSizeSection`, so they mount only when the gate is on
// (normal client-side mount, not a hydration mismatch).
const HeroPersonalized = dynamic(() => import('./HeroPersonalized'))
const CrossLink = dynamic(() => import('./CrossLink'))
const ContentHub = dynamic(() => import('./ContentHub'))
const RentSpace = dynamic(() => import('./RentSpace'))
const SizeSection = dynamic(() => import('./SizesSection'))
const BlogSection = dynamic(() => import('./BlogSection'))
const ServicesSection = dynamic(() => import('../Landing/ServicesSection'))
const SpecialistSection = dynamic(() => import('./SpecialistSection'))

// Sticky mobile CTA appears once the visitor has scrolled past the hero.
// 150px catches scanners on both paid and organic — earlier than the
// original 500px, but past the eyebrow so it doesn't compete with the H1.
const STICKY_CTA_THRESHOLD_PX = 150

// Defensive HTML-escape for i18n values rendered via dangerouslySetInnerHTML.
// i18n keys are author-controlled (locale JSON in this repo), but escape
// `<>&"` so a translator quoting an angle bracket can't break out.
// See CLAUDE.md → "Trigger H — Facebook In-App Browser strips boundary comments".
const escapeForHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const B2B_PILLARS: ReadonlySet<PillarPage> = new Set([
  PillarPage.Warehouse,
  PillarPage.Industrial
])

type IProps = {
  pillar: PillarPage
  firstBlogPost: IPost | null
  /** Optional SSR-resolved city from the `?city=<slug>` URL param. When
   *  provided, the personalized hero renders directly in SSR HTML — no
   *  client-side flash. Null for organic / SEO traffic. */
  initialCity?: SearchPageCity | null
}

const PersonalizedLanding = ({
  pillar,
  firstBlogPost,
  initialCity
}: IProps) => {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { showSizeSection, crossLink } = PILLAR_PAGE_MAP[pillar]

  const { city, cityName, source } = useEffectiveCity(initialCity)
  const isPaidWithCity = source === 'url' && cityName.length > 0
  const isB2B = B2B_PILLARS.has(pillar)

  // CTA target — paid traffic with explicit city goes to that city pillar;
  // organic / unknown traffic uses the closest geo-resolved city (or the
  // Monterrey fallback that `useEffectiveCity` provides).
  const ctaHref = useMemo(() => `/${pillar}/${city}`, [pillar, city])

  const [showStickyCta, setShowStickyCta] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > STICKY_CTA_THRESHOLD_PX
      const docHeight = document.documentElement.scrollHeight
      const nearBottom = window.scrollY + window.innerHeight > docHeight - 400
      setShowStickyCta(scrolled && !nearBottom)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const stickyLabel = isPaidWithCity
    ? t('HERO.PRIMARY_CTA')
    : t('HERO.STICKY_CTA')

  return (
    <div suppressHydrationWarning>
      {/* Hero — paid traffic with `?city=` gets the city-aware variant.
          Everyone else (SEO/organic) sees the generic hero with the
          autocomplete and Mexico-wide framing — same as before. */}
      {isPaidWithCity ? (
        <HeroPersonalized
          pillar={pillar}
          ctaHref={ctaHref}
          city={city}
          cityName={cityName}
        />
      ) : (
        <Hero
          pillar={pillar}
          ctaHref={ctaHref}
          city={city}
          cityName={cityName}
          cityKnown={source !== 'fallback' && cityName.length > 0}
        />
      )}

      {/* Cross-pillar disambiguation — only renders on SelfStorage and
          Warehouse pillars, the two that share the ambiguous "renta de
          bodegas" / "bodegas en renta" search intent. Internally gated;
          safe to render here for every pillar. City context is preserved
          only when the visitor came in via `?city=` so organic pillar
          landings stay anchored at the pillar root. */}
      <PillarDisambiguation
        pillar={pillar}
        currentCity={isPaidWithCity ? city : undefined}
      />

      {/* Listings carousel — promoted to position #2 for both audiences.
          Paid traffic uses deterministic city center + eager-load so the
          carousel paints fast; organic stays scroll-gated and uses the
          visitor's geo. */}
      <RecommendedSection
        surface="pillar_recommended"
        pillar={pillar}
        ctaHref={ctaHref}
        effectiveCity={isPaidWithCity ? city : undefined}
        eagerLoad={isPaidWithCity}
      />

      {/* Renter proof, chosen per pillar: pensión gets the vehicle-only
          roster (dealerships ARE the reassurance there), storage and
          industrial get the mixed one. See `bandForPillar`. */}
      <Logos
        band={bandForPillar(pillar)}
        caption={captionForPillar(pillar)}
        className="border-t border-ink-100/15 bg-cream-300 py-10"
      />

      <Features pillar={pillar} ctaHref={ctaHref} />

      {/* Almacenamiento a Domicilio cross-link — only renders on the
          SelfStorage pillar, and only when at least one on-demand market
          has `partnerActive: true`. The component is pillar-gated internally
          so it short-circuits for Parking / Warehouse / Industrial. The
          city-aware variant kicks in when the visitor came in via `?city=`
          and that city resolves to a covered, partner-active market. */}
      <CrossLinkOnDemand
        pillar={pillar}
        currentCity={isPaidWithCity ? city : undefined}
      />

      {/* Soluciones Logísticas (3PL) cross-link — nationwide, always
          visible; internally gated to Warehouse/Industrial pillars only
          (no-ops on SelfStorage/Parking, where CrossLinkOnDemand above
          already owns the equivalent slot). */}
      <CrossLinkLogistics pillar={pillar} />

      {/* B2B pillars (warehouse, industrial) get a dedicated specialist
          section with form + WhatsApp fallback in this slot. SelfStorage
          and Parking keep the lighter mid-page conversion nudge below. */}
      {isB2B ? (
        <SpecialistSection pillar={pillar} />
      ) : (
        <section className="border-y border-ink-100/15 bg-cream-300 px-5 py-14 md:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('HERO.MID_CTA_EYEBROW')}
            </p>
            {/* Trigger H fix (CLAUDE.md): the fragment-split pattern
                (.replace + .split + .map with React.Fragment + span) emits the
                same React tree shape as a manual {prefix}{' '}<span>{italic}</span>{' '}{suffix}
                — adjacent text/element/text children produce React boundary
                comments `<!-- -->` at SSR which Facebook in-app browser strips
                before hydration → React 19 #418. dangerouslySetInnerHTML emits
                one continuous text node so there are no boundary comments to
                strip. Escape `<>&"` defensively in case a translator quotes
                an angle bracket in the locale JSON. */}
            <h2
              className="mt-4 font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]"
              dangerouslySetInnerHTML={{
                __html: escapeForHtml(t('HERO.MID_CTA_TITLE')).replace(
                  escapeForHtml(t('HERO.MID_CTA_TITLE_ITALIC')),
                  `<span class="font-display text-blue-500 italic">${escapeForHtml(t('HERO.MID_CTA_TITLE_ITALIC'))}</span>`
                )
              }}
            />
            <p className="mx-auto mt-4 max-w-xl text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
              {t('HERO.MID_CTA_SUBTITLE')}
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 md:flex-row">
              <a
                href={ctaHref}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-500 px-7 py-3 text-base font-semibold text-white-500 transition-all duration-200 hover:bg-blue-600 active:scale-[0.98]"
              >
                {isPaidWithCity
                  ? t('HERO.PRIMARY_CTA_CITY', { city: cityName })
                  : t('HERO.MID_CTA_BUTTON')}
              </a>
            </div>
          </div>
        </section>
      )}

      <Comparison pillar={pillar} />

      {/* Host CTA — irrelevant for paid renter traffic; keep for organic
          where the host audience is part of the SEO funnel. */}
      {!isPaidWithCity && <RentSpace pillar={pillar} ctaHref={ctaHref} />}

      {showSizeSection && <SizeSection pillar={pillar} ctaHref={ctaHref} />}

      {/* Long-form SEO content — only for organic. Paid traffic with intent
          shouldn't be force-fed pricing/nearshoring/comparison-types essays
          on mobile; it just inflates scroll. */}
      {!isPaidWithCity && (
        <>
          <ContentHub pillar={pillar} section="pricing" />
          <ContentHub pillar={pillar} section="nearshoring" />
          <ContentHub pillar={pillar} section="comparison_types" />
        </>
      )}

      {/* `crossLink` copy lives in `searchPages.tsx`, a cross-repo mirror we
          deliberately keep byte-identical with the server — so the locale
          override is applied here at the render site instead. `defaultValue`
          falls back to the ES constant for any pillar without the key. */}
      {crossLink && (
        <CrossLink
          {...crossLink}
          title={t('CROSS_LINK.TITLE', { defaultValue: crossLink.title })}
          subtitle={t('CROSS_LINK.SUBTITLE', {
            defaultValue: crossLink.subtitle
          })}
        />
      )}

      <FaqsSection
        pillar={pillar}
        currentCity={isPaidWithCity ? city : undefined}
      />

      {/* More long-form SEO + cross-promotional content — organic only. */}
      {!isPaidWithCity && (
        <>
          <ContentHub pillar={pillar} section="intro" />
          <ContentHub pillar={pillar} section="cities" />
          {/* Almacenamiento a Domicilio tile — only renders on the SelfStorage
              pillar with at least one partner-active market. Internally pillar-
              and coverage-gated; safe to render here unconditionally. The
              tile sits after the cities cluster in the long-form SEO block so
              it reads as a related-resource cross-link, not a primary CTA. */}
          {/* `currentCity` is deliberately omitted: this block only renders
              when `!isPaidWithCity`, so the previous
              `isPaidWithCity ? city : undefined` ternary was always
              `undefined` — dead code that read as if the tile could be
              city-aware here. The organic tile links to the parent
              on-demand landing; the city-aware variant belongs to
              `CrossLinkOnDemand` above, which runs outside this guard. */}
          <ContentHub pillar={pillar} section="on_demand" />
          <ServicesSection
            ctaHref={ctaHref}
            className="border-t border-ink-100/15 bg-white-500"
          />
          <BlogSection firstBlogPost={firstBlogPost} pillar={pillar} />
        </>
      )}

      {/* Sticky mobile CTA */}
      <div
        className={`fixed inset-x-0 bottom-0 z-50 border-t border-ink-100/15 bg-white-500/95 px-4 py-3 backdrop-blur-sm transition-all duration-300 md:hidden ${
          showStickyCta
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-full opacity-0'
        }`}
      >
        <a
          href={ctaHref}
          className="flex w-full items-center justify-center rounded-full bg-blue-500 py-3 text-center text-sm font-semibold text-white-500 shadow-lg shadow-blue-500/20 transition-colors hover:bg-blue-600 active:scale-[0.98]"
        >
          {stickyLabel}
        </a>
      </div>
    </div>
  )
}

export default React.memo(PersonalizedLanding)
